import {
  Component,
  input,
  OnDestroy,
  output,
  TemplateRef,
  viewChild,
  signal,
  computed
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import Pubnub from 'pubnub';
import { merge, Subject, Subscription, timer } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { differenceInYears } from 'date-fns';

import { IrisButtonContainerAlignments, IrisButtonTypes, IrisButtonIconPositions } from '@sondermind/iris/button';
import { IconSizes, IconColors } from '@sondermind/iris/icon';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';

import {
  OctaveHttpService,
  IOctaveDetails,
  IOctaveUpsertPatientRequest
} from '@sondermind/data-access/client-facing/billing';
import { Logger } from '@sondermind/datadog-logging';
import { PubnubLib } from '@sondermind/pubnub-lib';
import { ClientUserProfile } from '@sondermind/utilities/models-user-client';
import { SharedMasks, TextMasks } from '@sondermind/utilities/text-masks';
import { camelCaseTransformer } from '@sondermind/utilities/tools';
import { InfoLinks } from '@sondermind/core/enums/info-links.enum';

interface IEbcgPaymentMessage {
  partnerClientId: string;
  paymentMethodEntered: boolean;
}

interface IGuardianInfo {
  guardianFirstName: string;
  guardianLastName: string;
  guardianEmail: string;
  guardianPhone: string;
}

interface IEbcgAgreementFormValue {
  acceptedEbcgTerms: boolean;
  guardianInfo: IGuardianInfo;
}

const EBCG_PAYMENT_CHANNEL = 'ebcg_payment_details_received';
const PAYMENT_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

@Component({
  selector: 'client-setup-ebcg-payment-info',
  templateUrl: './setup-ebcg-payment-info.component.html',
  styleUrls: ['./setup-ebcg-payment-info.component.scss'],
  standalone: false
})
export class SetupEbcgPaymentInfoComponent implements OnDestroy {
  readonly useInSessionRequest = input<boolean>(false);
  readonly fromDirectBooking = input<boolean>(false);
  readonly providerName = input<string>('[Provider Name]');
  readonly userProfile = input<ClientUserProfile | undefined>(undefined);
  readonly paymentInfoSubmitted = output();
  readonly exitSetup = output();

  // Template for the action button that parent component can render
  readonly actionButtonTemplate = viewChild.required<TemplateRef<unknown>>('actionButton');

  readonly ebcgTermsOfService = InfoLinks.EVERNORTH_TERMS_OF_SERVICE;
  readonly ebcgPrivacyPolicy = InfoLinks.EVERNORTH_PRIVACY_POLICY;

  readonly submitting = signal<boolean>(false);
  readonly octaveDetails = signal<IOctaveDetails | null>(null);
  readonly loading = signal<boolean>(false);
  readonly waitingForPayment = signal<boolean>(false);
  readonly paymentCompleted = signal<boolean>(false);
  readonly paymentFailed = signal<boolean>(false);

  IrisButtonContainerAlignments = IrisButtonContainerAlignments;
  IrisButtonTypes = IrisButtonTypes;
  IrisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;
  IrisButtonIconPositions = IrisButtonIconPositions;
  IrisIconSizes = IconSizes;
  IconColors = IconColors;

  masks: TextMasks = SharedMasks;
  phoneNumberMask = this.masks['phoneMask'];

  ebcgAgreementForm!: FormGroup;
  private readonly acceptedTerms = signal(false);
  private readonly guardianInfoValid = signal(true); // Default to true for users 18+
  private destroyed$ = new Subject<void>();

  private paymentSubscription?: Subscription;
  private formSubscription?: Subscription;
  private pubnubListener?: Pubnub.ListenerParameters;

  /**
   * Get the client's full name (preferred first name or first name + last name)
   */
  readonly clientName = computed(() => {
    const profile = this.userProfile();
    if (!profile) {
      return '[Client name]';
    }
    const firstName = profile.preferredFirstName || profile.firstName || '';
    const lastName = profile.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || '[Client name]';
  });

  /**
   * Check if client is under 18 years old based on birth date
   */
  readonly isUnder18 = computed(() => {
    const birthDate = this.userProfile()?.birthDate;

    if (!birthDate) {
      return false;
    }

    const age = differenceInYears(new Date(), new Date(birthDate));
    return age < 18;
  });

  /**
   * Enable submit button when client has checked box accepting EBCG terms
   * and guardian info is valid if user is under 18
   */
  readonly allowSubmit = computed(() => {
    const isGuardianInfoValid = this.isUnder18() ? this.guardianInfoValid() : true;

    return this.acceptedTerms() && !this.submitting() && !this.loading() && isGuardianInfoValid;
  });

  constructor(
    private fb: FormBuilder,
    private octaveHttpService: OctaveHttpService,
    private pubnubLib: PubnubLib,
    private router: Router,
    private logger: Logger
  ) {
    this.initializeForm();

    // Convert form value and status changes to signals for reactive change detection
    this.formSubscription = merge(
      this.ebcgAgreementForm.valueChanges,
      this.ebcgAgreementForm.statusChanges
    ).pipe(
      startWith(null),
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      const value = this.ebcgAgreementForm.getRawValue() as IEbcgAgreementFormValue;
      this.acceptedTerms.set(!!value?.acceptedEbcgTerms);
      // Update guardian form validity signal
      const guardianFormGroup = this.ebcgAgreementForm.get('guardianInfo');
      this.guardianInfoValid.set(guardianFormGroup?.valid ?? true);
    });
  }

  ngOnDestroy(): void {
    this.cleanupPaymentListener();
    this.formSubscription?.unsubscribe();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Load Octave client details from the backend
   * Called after upsertPatient$ completes successfully
   */
  private loadOctaveDetails(): void {
    this.loading.set(true);
    this.octaveHttpService.getDetails$().pipe(
      takeUntil(this.destroyed$)
    ).subscribe({
      next: (details) => {
        this.octaveDetails.set(details);
        this.loading.set(false);
        this.submitting.set(false);

        // Open payment window with the billing portal URL
        const ebcgPaymentUrl = details.ebcgBillingPortalUrl;
        if (ebcgPaymentUrl) {
          window.open(ebcgPaymentUrl, '_blank');
        }

        // Start listening for payment completion via PubNub
        this.waitingForPayment.set(true);
        this.listenForPaymentCompletion();
      },
      error: () => {
        this.loading.set(false);
        this.submitting.set(false);
        this.paymentFailed.set(true);
      }
    });
  }

  initializeForm(): void {
    this.ebcgAgreementForm = this.fb.group({
      acceptedEbcgTerms: [false],
      guardianInfo: this.fb.group({
        guardianFirstName: ['', [Validators.required]],
        guardianLastName: ['', [Validators.required]],
        guardianEmail: ['', [Validators.required, Validators.email]],
        guardianPhone: ['']
      })
    });
  }

  /**
   * Navigate to care team page after payment error and close account setup flow
   */
  navigateToCareTeamPage(): void {
    // Reset error state
    this.paymentFailed.set(false);

    // Emit exitSetup event to close the flow
    this.exitSetup.emit();

    // For standalone flow (not direct booking), navigate to care team page
    if (!this.fromDirectBooking()) {
      this.router.navigate(['/care-team/providers']);
    }
  }

  /**
   * Open external payment provider link
   */
  openPaymentProvider(): void {
    if (!this.allowSubmit()) {
      return;
    }

    // Reset error state
    this.paymentFailed.set(false);

    this.submitting.set(true);

    const formValue = this.ebcgAgreementForm.getRawValue() as IEbcgAgreementFormValue;
    const requestBody: IOctaveUpsertPatientRequest = {
      signedEbcgTos: !!formValue.acceptedEbcgTerms
    };

    // Include guardian information if user is under 18
    if (this.isUnder18()) {
      requestBody.guardianFirstName = formValue.guardianInfo.guardianFirstName;
      requestBody.guardianLastName = formValue.guardianInfo.guardianLastName;
      requestBody.guardianEmail = formValue.guardianInfo.guardianEmail;
      requestBody.guardianPhone = formValue.guardianInfo.guardianPhone;
    }

    // Call upsertPatient first to update the record
    this.octaveHttpService.upsertPatient$(requestBody).pipe(
      takeUntil(this.destroyed$)
    ).subscribe({
      next: () => {
        // After upsert succeeds, get the details which now include the billing portal URL
        this.loadOctaveDetails();
      },
      error: () => {
        // Show error page when upsertPatient fails
        this.submitting.set(false);
        this.paymentFailed.set(true);
        this.waitingForPayment.set(false);
      }
    });
  }

  /**
   * Subscribe to PubNub channel to listen for payment completion messages
   */
  private listenForPaymentCompletion(): void {
    const personaClientId = this.octaveDetails()?.personaClientId;

    // Subscribe to the EBCG payment channel
    this.pubnubLib.pubnub.subscribe({ channels: [EBCG_PAYMENT_CHANNEL] });

    // Set up listener for incoming messages
    this.pubnubListener = {
      message: (event: Pubnub.MessageEvent) => {
        if (event.channel === EBCG_PAYMENT_CHANNEL) {
          // Transform snake_case message to camelCase
          const transformedMessage = camelCaseTransformer<IEbcgPaymentMessage>(event.message);
          this.handlePaymentMessage(transformedMessage, personaClientId);
        }
      }
    };

    this.pubnubLib.pubnub.addListener(this.pubnubListener);

    // Set up timeout - if no response in 15 minutes, show timeout message
    this.paymentSubscription = timer(PAYMENT_TIMEOUT_MS).pipe(
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      this.logger.warn('EBCG payment channel timed out waiting for payment completion', {
        component: 'SetupEbcgPaymentInfoComponent',
        timeoutMs: PAYMENT_TIMEOUT_MS,
        personaClientId
      });
      this.paymentFailed.set(true);
      this.waitingForPayment.set(false);
      this.cleanupPaymentListener();
    });
  }

  /**
   * Handle incoming payment messages from PubNub
   * Backend sends message with format: { partner_client_id: number, payment_method_entered: boolean }
   * partner_client_id corresponds to our persona_client_id
   * Message is transformed from snake_case to camelCase before reaching this method
   */
  private handlePaymentMessage(message: IEbcgPaymentMessage, personaClientId: number | undefined): void {
    // Only process messages for this specific client
    // Compare partnerClientId (number from message) with personaClientId (number from OctaveDetails)
    if (!personaClientId || message.partnerClientId !== personaClientId.toString()) {
      return;
    }

    if (message.paymentMethodEntered === true) {
      this.paymentCompleted.set(true);
      this.waitingForPayment.set(false);
      this.cleanupPaymentListener();
      // Emit success - parent component will handle next steps (session confirmation or account completion)
      this.paymentInfoSubmitted.emit();
    } else {
      this.logger.warn('EBCG payment channel received message with paymentMethodEntered false', {
        component: 'SetupEbcgPaymentInfoComponent',
        message,
        personaClientId
      });
      this.paymentFailed.set(true);
      this.waitingForPayment.set(false);
      this.cleanupPaymentListener();
    }
  }

  /**
   * Clean up PubNub subscription and timer
   */
  private cleanupPaymentListener(): void {
    if (this.pubnubListener) {
      this.pubnubLib.pubnub.removeListener(this.pubnubListener);
      this.pubnubLib.pubnub.unsubscribe({ channels: [EBCG_PAYMENT_CHANNEL] });
      this.pubnubListener = undefined;
    }
    this.paymentSubscription?.unsubscribe();
  }
}
